import axios from 'axios';
import { get } from 'lodash';
import { Equipment, EquipmentRecord } from '../interfaces/equipment';
import {
  parseResult,
  removeEmpty,
  userHasRole,
  wait,
} from '../../utils';
import { store } from '../../app.component';
import { User } from '../interfaces/user';
import { instance } from './instance';
import getConfig, { getUrl } from '../../config';

export const getEquipment = async (id?: string): Promise<Equipment> => {
  const resp = await instance.get(`/equipment/id/${id}`, { data: {} });
  return get(resp, 'data.equipment', null) as Equipment;
};

export const getEquipmentRecord = async (id: string, fullStatus = false) => {
  const resp = await instance.get(`/equipment/record/${id}${fullStatus ? '?fullStatus=true' : ''}`, { data: {} });
  return get(resp, 'data.equipment', null) as EquipmentRecord;
};

export const getEquipments = async (
  where?: {},
  filterOutShowOnDash = true,
  fromState = true
): Promise<Equipment[]> => {
  if (fromState) {
    const { dash } = store.getState();

    if (get(dash, 'view.equipmentLoading')) {
      await wait(1000);
      return (await getEquipments(
        where,
        filterOutShowOnDash,
        fromState
      )) as Equipment[];
    }

    const filtered = filterOutShowOnDash
      ? dash.equipment.filter((e: any) => get(e, 'showOnDash'))
      : dash.equipment;
    return where ? parseResult(where, filtered) : filtered;
  }

  let resp = {};
  if (where && Object.keys(where).length > 0) {
    resp = await instance.post(`/equipment`, {
      where,
    });
  } else {
    resp = await instance.get(`/equipment`);
  }
  let eq = get(resp, 'data.equipment', []) as Equipment[];
  return ((filterOutShowOnDash
    ? eq.filter(e => get(e, `showOnDash`))
    : eq) as unknown) as Equipment[];
};

export const getEquipmentForUser = async (user?: User) => {
  if (!user) {
    return [];
  }

  const isSuper = userHasRole([0, 5], store.getState());

  return await getEquipmentRecordsForUser(user.userId, !isSuper);
};

export const getEquipmentRecordsForUser = async (
  userId: string,
  filterOutShowOnDash = false
) => {
  // const resp = { data: require('../../assets/response.json') }; //use to prevent long request
  const resp = await instance.get(`/equipment/foruser/${userId}`);
  let eq = (get(resp, 'data.equipment', []) as EquipmentRecord[]);
  return ((filterOutShowOnDash
    ? eq.filter(e => get(e, `showOnDash`))
    : eq) as unknown) as EquipmentRecord[];
};

export const getEquipmentRecords = async (
  where?: {},
  filterOutShowOnDash = true
) => {
  let resp = {};
  if (where && Object.keys(where).length > 0) {
    resp = await instance.post(`/equipment/record`, {
      where,
    });
  } else {
    resp = await instance.get(`/equipment/record`);
  }
  let eq = get(resp, 'data.equipment', []) as EquipmentRecord[];
  return ((filterOutShowOnDash
    ? eq.filter(e => get(e, `equipment.showOnDash`))
    : eq) as unknown) as EquipmentRecord[];
};

export const createOrUpdateEquipment = async (equipment: Equipment) => {
  const resp = await instance.post('/equipment/create', {
    // AER-1701 (formerly AER-1702's keepEmptyStrings opt-in, now the default):
    // '' and null travel to the server so a cleared field wins the updateEquipment()
    // merge ({ ...equipmentRecord, ...eq }); createItem()'s toDynamoItem() then
    // removes the attribute for real. removeEmpty() only strips undefined.
    equipment: removeEmpty(equipment),
  });
  return get(resp, 'data.equipment', null) as Equipment;
};

// AER-1804: the server writes equipment status and ignores it in /equipment/create, so a test
// failed on the Manufacturing screen is recorded here, with its completion.
export const recordFailedTest = async (deviceId: string) => {
  const resp = await instance.post('/equipment/testfailed', {
    deviceId,
    // filled in by the request interceptor, as on every save
    updatedBy: '',
    updatedAt: '',
  });
  return get(resp, 'data.equipment', null) as Equipment;
};

export const deleteEquipment = async (equipment: Equipment) => {
  const resp = await instance.delete(`/equipment/id/${equipment.sk}`);
  return get(resp, 'data.equipment', null) as Equipment;
};

export const getEquipmentBySN = async (sn: string, activationcode?: string) => {
  // AER-1589: pass the activation code through so the server can pick the
  // correct record when two active records share the same equipmentSN.
  const qs = activationcode ? `?activationcode=${encodeURIComponent(activationcode)}` : '';
  const resp = await instance.get(`/equipment/sn/${sn}${qs}`, { data: {} });
  return get(resp, 'data.equipment', null) as EquipmentRecord;
}

// AER-1755: display-only equipment lookup, used by the purple header bar on the
// pre-login Equipment Registration screens (Login / New User Sign Up). It
// deliberately does NOT go through the shared `instance`: that instance's 401
// response interceptor clears localforage, alerts, and force-reloads the page
// (the AER-1589 regression), which would be destructive on a screen the user
// has not signed in on yet. Callers pass the bootstrap token minted by
// loadToken() and must treat any failure as simply "no header bar".
export const getEquipmentDisplayInfoBySN = async (
  sn: string,
  token: string,
  activationcode?: string
): Promise<EquipmentRecord | null> => {
  const qs = activationcode ? `?activationcode=${encodeURIComponent(activationcode)}` : '';
  const resp = await axios.get(`${getUrl()}/equipment/sn/${encodeURIComponent(sn)}${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return get(resp, 'data.equipment', null) as EquipmentRecord | null;
};

export const equipmentWasRegistered = async (equipment: Equipment[]) => {
  const config = getConfig()
  const equipments = equipment.filter(e => e && e.deviceId).map(e => e.deviceId);
  const resp = await instance({
    method: 'post',
    url: '/oracle/equipment/registered', 
    headers: {
      accesstoken: config.access_token,
    },
    data: {
      equipment: equipments,
    }
  });
  return get(resp, 'data.equipment', null) as Equipment;
}